import { bool, func, number } from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ReactSelect from 'react-select'
import styled from 'styled-components'
const StyledElement = styled.div`
	align-items: center;
	display: flex;
	& h4 {
		font-size: 16px;
		font-weight: 600;
		margin: 0 0 0 16px;
	}
`
const selectOptions = {
	components: { IndicatorSeparator: () => null },
	isClearable: false,
	isMulti: false,
	isSearchable: false,
	maxMenuHeight: 230,
	menuPlacement: 'top',
	menuPortalTarget: document.body,
	options: [
		{
			label: '10',
			value: 10,
		},
		{
			label: '15',
			value: 15,
		},
		{
			label: '20',
			value: 20,
		},
		{
			label: '30',
			value: 30,
		},
		{
			label: '50',
			value: 50,
		},
		{
			label: '100',
			value: 100,
		},
	],
	styles: {
		control: styles => ({
			...styles,
			backgroundColor: 'transparent',
			border: '1px solid #e5e7eb',
			borderRadius: 20,
			boxShadow: 'none',
			color: '#ACACAC',
			cursor: 'pointer',
			height: 40,
			minHeight: 40,
			minWidth: 70,
			outline: 'none',
			padding: 0,
			width: '100%',
			transition: 0,
			':hover': {
				border: '1px solid #e5e7eb',
			},
			'.dark &': {
				border: '1px solid #5e5e5e',
				':hover': {
					border: '1px solid #5e5e5e',
				},
			},
		}),
		valueContainer: styles => ({
			...styles,
			display: 'flex',
			height: 40,
			padding: '9px 4px 9px 16px',
		}),
		singleValue: (styles, { data }) => ({
			...styles,
			color: data?.isDisabled ? '#696f85' : '#252a3b',
			fontSize: 16,
			fontWeight: 600,
			margin: 0,
			transition: 0,
			'.dark &': {
				color: data?.isDisabled ? '#aaaaaa' : '#e1e1e1',
			},
		}),
		placeholder: styles => ({
			...styles,
			fontSize: 16,
			fontWeight: 600,
			margin: 0,
		}),
		indicatorsContainer: styles => ({ ...styles, padding: '0 10px 0 8px' }),
		dropdownIndicator: styles => ({
			...styles,
			alignItems: 'center',
			backgroundColor: 'transparent',
			borderRadius: 13,
			color: '#949494',
			display: 'flex',
			height: 23,
			justifyContent: 'center',
			margin: 0,
			padding: 0,
			width: 23,
			svg: {
				width: 18,
			},
			':hover': {
				color: '#949494',
			},
			'.dark &': {
				color: '#aaaaaa',
				':hover': {
					color: '#aaaaaa',
				},
			},
		}),
		menu: styles => ({
			...styles,
			backgroundColor: '#ffffff',
			border: 'none',
			borderRadius: 12,
			boxShadow:
				'0 1px 20px 0 rgba(13, 46, 105, 0.07), 0 1px 20px 0 rgba(13, 46, 105, 0.07)',
			margin: 0,
			overflow: 'hidden',
			padding: 0,
			'.dark &': {
				backgroundColor: '#333333',
				boxShadow:
					'0 1px 20px 0px rgba(0, 0, 0, 0.07), 0 1px 20px 0px rgba(0, 0, 0, 0.07)',
			},
		}),
		menuPortal: styles => ({ ...styles, zIndex: 9999 }),
		menuList: styles => ({
			...styles,
			padding: 4,
			'::-webkit-scrollbar': {
				padding: '0 5px 0 0',
				width: 6,
			},
			'::-webkit-scrollbar-track': {
				backgroundColor: 'transparent',
			},
			'::-webkit-scrollbar-thumb': {
				backgroundColor: '#018ECC',
				borderRadius: 3,
			},
		}),
		option: (styles, { isSelected, isDisabled, isFocused }) => ({
			...styles,
			backgroundColor: isSelected
				? '#018ECC'
				: isFocused
				? '#f1f1f1'
				: '#ffffff',
			borderRadius: 8,
			color: isSelected ? '#ffffff' : isFocused ? '#252a3b' : '#252a3b',
			cursor: isDisabled ? 'not-allowed' : 'pointer',
			fontSize: 16,
			fontWeight: 500,
			height: 36,
			overflow: 'hidden',
			padding: '8px 12px',
			textOverflow: 'ellipsis',
			transition: 200,
			transitionTimingFunction: 'cubic-bezier(0, 0, 1, 1)',
			whiteSpace: 'nowrap',
			width: '100%',
			':hover': {
				backgroundColor: isSelected ? '#018ECC' : '#f1f1f1',
			},
			'.dark &': {
				color: '#ffffff',
				backgroundColor: isSelected
					? '#018ECC'
					: isFocused
					? '#41932133'
					: 'transparent',
				':hover': {
					backgroundColor: isSelected ? '#018ECC' : '#41932133',
				},
			},
		}),
	},
}
const Pagination = ({
	totalItemsCount = 1,
	isDisabled = false,
	onChange,
	page = 1,
	page_size = 1,
}) => {
	const { i18n } = useTranslation()
	const { language } = i18n
	const value = useMemo(() => {
		const value = { label: page_size, value: page_size }
		return value
	}, [page_size])
	const state = useMemo(() => {
		const end =
			page * page_size > totalItemsCount ? totalItemsCount : page * page_size
		const start = (page - 1) * page_size + 1
		const suffix = language === 'uz' ? '/' : 'из'
		const state = `${start}-${end} ${suffix} ${totalItemsCount}`
		return state
	}, [language, page, page_size, totalItemsCount])
	return (
		<StyledElement>
			<ReactSelect
				{...selectOptions}
				isDisabled={isDisabled}
				value={value}
				onChange={option => {
					onChange({ page: 1, page_size: option?.value })
				}}
			/>
			<h4 className='text-[#3c3c3c] dark:text-[#e1e1e1]'>{state}</h4>
		</StyledElement>
	)
}
Pagination.propTypes = {
	count: number,
	isDisabled: bool,
	onChange: func,
	page: number,
	page_size: number,
}
export default Pagination
